import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    LifeBuoy, MessageCircle, RefreshCw, Image as ImageIcon,
    CheckCircle2, Circle, AlertCircle, Lock,
} from "lucide-react";

/**
 * /tickets — every authenticated user's personal helpdesk.
 *
 * Lists their tickets (newest-first), opens a thread view where they can read
 * admin replies and post follow-ups. Replying on a `resolved` ticket reopens
 * it (handled server-side).
 */
const STATUS_META = {
    open: { label: "Open", icon: Circle, cls: "border-amber-300 bg-amber-50 text-amber-900" },
    in_progress: { label: "In progress", icon: RefreshCw, cls: "border-ikb/40 bg-ikb/5 text-ikb" },
    resolved: { label: "Resolved", icon: CheckCircle2, cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
    closed: { label: "Closed", icon: Lock, cls: "border-border bg-secondary text-foreground/60" },
};

export default function MyTickets() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);  // ticket id

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/tickets/mine");
            setTickets(data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="max-w-screen-lg mx-auto px-3 sm:px-6 py-4 sm:py-6">
            <div className="flex items-center gap-2 mb-4">
                <LifeBuoy className="w-5 h-5 text-ikb" />
                <h1 className="text-2xl font-display font-bold tracking-tight">My tickets</h1>
                <Button
                    onClick={load}
                    variant="outline"
                    size="sm"
                    className="ml-auto rounded-none"
                    disabled={loading}
                    data-testid="my-tickets-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {loading && tickets.length === 0 ? (
                <div className="text-foreground/60 text-sm py-12 text-center">Loading…</div>
            ) : tickets.length === 0 ? (
                <div className="border border-dashed border-border p-8 text-center text-foreground/60">
                    No tickets yet. Click <strong>Feedback</strong> in the top bar to file one.
                </div>
            ) : (
                <div className="border border-border bg-white" data-testid="my-tickets-list">
                    <div className="grid grid-cols-12 gap-3 px-3 py-2 border-b border-border bg-secondary/40 text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                        <div className="col-span-5">Description</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-2">Category</div>
                        <div className="col-span-3">Updated</div>
                    </div>
                    {tickets.map((t) => {
                        const sm = STATUS_META[t.status] || STATUS_META.open;
                        const SIcon = sm.icon;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActive(t.id)}
                                className="w-full grid grid-cols-12 gap-3 px-3 py-3 border-b border-border last:border-b-0 hover:bg-secondary/30 text-left"
                                data-testid={`my-ticket-${t.id}`}
                            >
                                <div className="col-span-5 min-w-0">
                                    <div className="text-sm font-medium truncate">{t.description}</div>
                                    {(t.responses || []).length > 0 ? (
                                        <div className="text-[11px] text-foreground/60 flex items-center gap-1 mt-0.5">
                                            <MessageCircle className="w-3 h-3" /> {(t.responses || []).length} repl{(t.responses || []).length === 1 ? "y" : "ies"}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="col-span-2">
                                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 ${sm.cls}`}>
                                        <SIcon className="w-3 h-3" /> {sm.label}
                                    </span>
                                </div>
                                <div className="col-span-2 text-[11px] font-mono text-foreground/70">{t.category || "other"}</div>
                                <div className="col-span-3 text-[11px] text-foreground/60">{new Date(t.updated_at).toLocaleString()}</div>
                            </button>
                        );
                    })}
                </div>
            )}

            <TicketThreadDialog
                ticketId={active}
                onClose={() => setActive(null)}
                onChanged={load}
                isAdminMode={false}
            />
        </div>
    );
}

/**
 * Shared thread dialog — used by both /tickets (user mode) and /admin/helpdesk
 * (admin mode). Admin mode shows the status/priority controls and lets admins
 * see who the ticket belongs to.
 */
export function TicketThreadDialog({ ticketId, onClose, onChanged, isAdminMode }) {
    const [ticket, setTicket] = useState(null);
    const [reply, setReply] = useState("");
    const [busy, setBusy] = useState(false);
    const [screenshotUrl, setScreenshotUrl] = useState(null);

    const load = useCallback(async () => {
        if (!ticketId) return;
        try {
            const { data } = await api.get(`/tickets/${ticketId}`);
            setTicket(data);
            // If there's a screenshot path and we're admin, resolve a viewable URL
            if (data.screenshot_path) {
                // We use the existing storage download proxy at /api/storage/download?path=...
                // (fall back to the bare path if no proxy is configured).
                setScreenshotUrl(`${api.defaults.baseURL || ""}/storage/download?path=${encodeURIComponent(data.screenshot_path)}`);
            } else {
                setScreenshotUrl(null);
            }
        } catch (err) { toast.error(formatApiError(err)); }
    }, [ticketId]);

    useEffect(() => { load(); }, [load]);

    if (!ticketId) return null;

    const sendReply = async () => {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            await api.post(`/tickets/${ticketId}/respond`, { message: reply.trim() });
            setReply("");
            await load();
            onChanged?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const setStatus = async (newStatus) => {
        setBusy(true);
        try {
            await api.patch(`/admin/tickets/${ticketId}`, { status: newStatus });
            await load();
            onChanged?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const setPriority = async (newPriority) => {
        setBusy(true);
        try {
            await api.patch(`/admin/tickets/${ticketId}`, { priority: newPriority });
            await load();
            onChanged?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={!!ticketId} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent className="max-w-2xl" data-testid="ticket-thread-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 flex-wrap">
                        <LifeBuoy className="w-4 h-4 text-ikb" />
                        {ticket?.category ? <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-border bg-secondary text-foreground/70">{ticket.category}</span> : null}
                        Ticket
                        {ticket?.status ? (
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${(STATUS_META[ticket.status] || STATUS_META.open).cls}`}>
                                {(STATUS_META[ticket.status] || STATUS_META.open).label}
                            </span>
                        ) : null}
                    </DialogTitle>
                </DialogHeader>

                {!ticket ? (
                    <div className="text-sm text-foreground/60 py-6">Loading…</div>
                ) : (
                    <div className="space-y-3">
                        <div className="text-[11px] text-foreground/60">
                            From <strong className="text-foreground/80">{ticket.user_name || ticket.user_email}</strong>
                            {ticket.user_email && ticket.user_name ? <> &lt;{ticket.user_email}&gt;</> : null}
                            {ticket.page_url ? <> · on <a href={ticket.page_url} target="_blank" rel="noreferrer" className="text-ikb underline">{ticket.page_url.replace(/^https?:\/\//, "").slice(0, 60)}</a></> : null}
                            {" · "}
                            {new Date(ticket.created_at).toLocaleString()}
                        </div>

                        <div className="border border-border bg-secondary/30 p-3 text-sm whitespace-pre-wrap">{ticket.description}</div>

                        {screenshotUrl ? (
                            <a href={screenshotUrl} target="_blank" rel="noreferrer" className="block border border-border p-2 hover:bg-secondary/30">
                                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-1">
                                    <ImageIcon className="w-3 h-3" />
                                    Screenshot
                                </div>
                                <img
                                    src={screenshotUrl}
                                    alt="Ticket screenshot"
                                    className="max-h-64 w-full object-contain bg-white"
                                    data-testid="ticket-screenshot"
                                />
                            </a>
                        ) : null}

                        {isAdminMode ? (
                            <div className="flex items-center gap-2 flex-wrap border-t border-border pt-3">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Status:</span>
                                {Object.keys(STATUS_META).map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setStatus(s)}
                                        disabled={busy || ticket.status === s}
                                        className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${ticket.status === s ? STATUS_META[s].cls + " font-bold" : "border-border bg-white hover:bg-secondary text-foreground/70"} disabled:opacity-50`}
                                        data-testid={`ticket-set-status-${s}`}
                                    >
                                        {STATUS_META[s].label}
                                    </button>
                                ))}
                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 ml-2">Priority:</span>
                                {["low", "medium", "high"].map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setPriority(p)}
                                        disabled={busy || ticket.priority === p}
                                        className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${ticket.priority === p ? "border-ikb bg-ikb/10 text-ikb font-bold" : "border-border bg-white hover:bg-secondary text-foreground/70"} disabled:opacity-50`}
                                        data-testid={`ticket-set-priority-${p}`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        ) : null}

                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                            {(ticket.responses || []).map((r) => (
                                <div key={r.id} className={`border-l-2 p-2 ${r.is_admin ? "border-ikb bg-ikb/5" : "border-emerald-400 bg-emerald-50/50"}`} data-testid={`ticket-response-${r.id}`}>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                                        {r.is_admin ? <span className="text-ikb font-bold">Support</span> : <span className="text-emerald-700 font-bold">You</span>} · {r.user_name || r.user_email} · {new Date(r.created_at).toLocaleString()}
                                    </div>
                                    <div className="text-sm whitespace-pre-wrap mt-0.5">{r.message}</div>
                                </div>
                            ))}
                        </div>

                        {ticket.status !== "closed" ? (
                            <div>
                                <Textarea
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    rows={3}
                                    placeholder={isAdminMode ? "Reply as support…" : "Add a reply…"}
                                    maxLength={4000}
                                    data-testid="ticket-reply-input"
                                />
                            </div>
                        ) : (
                            <div className="text-[11px] text-foreground/60 italic border border-border p-2">This ticket is closed.</div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="ticket-close">Close</Button>
                    {ticket?.status !== "closed" ? (
                        <Button
                            onClick={sendReply}
                            disabled={busy || !reply.trim()}
                            className="bg-ikb hover:bg-[#001f7a] text-white"
                            data-testid="ticket-send-reply"
                        >
                            Send reply
                        </Button>
                    ) : null}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
